import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Bold, 
  Italic, 
  Underline,
  List, 
  ListOrdered, 
  Quote, 
  Heading1, 
  Heading2, 
  Heading3, 
  Undo, 
  Redo, 
  Palette, 
  Highlighter, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  ChevronDown,
  Eraser,
  Type,
  ChevronUp,
  Link,
  Unlink,
  IndentIncrease,
  IndentDecrease
} from 'lucide-react';

const colors = [
  { name: 'Default', value: '#262626' },
  { name: 'Crimson', value: '#B22A2F' },
  { name: 'Electric', value: '#2874A6' },
  { name: 'Emerald', value: '#2D7F5E' },
  { name: 'Gold', value: '#D9A441' },
  { name: 'Slate', value: '#4F5D75' },
  { name: 'Midnight', value: '#1C2B39' }
];

const highlights = [
  { name: 'Yellow', value: '#fef9c3' },
  { name: 'Green', value: '#dcfce7' },
  { name: 'Blue', value: '#dbeafe' },
  { name: 'Purple', value: '#f3e8ff' },
  { name: 'Pink', value: '#fce7f3' },
];

interface FormatBarProps {
  editor: any;
  isMobileView?: boolean;
}

// Helper function to check if the selection is inside a list
function isInList(editor: any): boolean {
  if (!editor) return false;
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
      return true;
    }
  }
  return false;
}

export function FormatBar({ editor, isMobileView = false }: FormatBarProps) {
  const [isColorOpen, setIsColorOpen] = useState(false);
  const [isHighlightOpen, setIsHighlightOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(isMobileView);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const colorMenuRef = useRef<HTMLDivElement>(null);
  const highlightMenuRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const formatBarRef = useRef<HTMLDivElement>(null);
  const linkModalRef = useRef<HTMLDivElement>(null);

  // Hide format bar when scrolling down, show when scrolling up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current + 10) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY.current - 10) {
        setIsVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle clicks outside of menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorMenuRef.current && !colorMenuRef.current.contains(event.target as Node)) {
        setIsColorOpen(false);
      }
      if (highlightMenuRef.current && !highlightMenuRef.current.contains(event.target as Node)) {
        setIsHighlightOpen(false);
      }
      if (linkModalRef.current && !linkModalRef.current.contains(event.target as Node)) {
        setShowLinkModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update collapsed state when mobile view changes
  useEffect(() => {
    setIsCollapsed(isMobileView);
  }, [isMobileView]);

  // Memoized handlers
  const clearFormatting = useCallback(() => {
    editor.chain().focus()
      .unsetAllMarks()
      .unsetHighlight()
      .setTextAlign('left')
      .run();
  }, [editor]);

  const setParagraph = useCallback(() => {
    editor.chain().focus().setParagraph().run();
  }, [editor]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const toggleColorMenu = useCallback(() => {
    setIsColorOpen(prev => !prev);
    if (isHighlightOpen) setIsHighlightOpen(false);
  }, [isHighlightOpen]);

  const toggleHighlightMenu = useCallback(() => {
    setIsHighlightOpen(prev => !prev);
    if (isColorOpen) setIsColorOpen(false);
  }, [isColorOpen]);

  const handleHighlight = useCallback((color: string) => {
    if (color === 'none') {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().setHighlight({ color }).run();
    }
    setIsHighlightOpen(false);
  }, [editor]);

  const handleLinkClick = useCallback(() => {
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, ' ');

    // Check if cursor is on an existing link
    const existingLink = editor.getAttributes('link');
    if (existingLink.href) {
      setLinkUrl(existingLink.href);
      setLinkText(text || '');
    } else {
      setLinkUrl('');
      setLinkText(text || '');
    }

    setShowLinkModal(true);
  }, [editor]);

  const handleLinkSubmit = useCallback(() => {
    if (!linkUrl) return;

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');

    if (selectedText) {
      // If text is selected, just add the link
      editor.chain().focus().setLink({ href: linkUrl }).run();
    } else {
      // If no text selected, insert new text with link
      const displayText = linkText || linkUrl;
      editor.chain().focus().insertContent(`<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${displayText}</a>`).run();
    }

    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
  }, [editor, linkText, linkUrl]);

  const handleUnlink = useCallback(() => {
    editor.chain().focus().unsetLink().run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div 
      ref={formatBarRef}
      className={`border-b bg-white p-2 flex flex-col sticky top-0 z-[var(--z-sticky)] shadow-sm transition-transform duration-200 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      {/* Mobile toggle button */}
      {isMobileView && (
        <div className="flex justify-center mb-2">
          <button
            onClick={toggleCollapsed}
            className="p-2 rounded-full bg-sand/10 text-slate hover:bg-sand/20"
            aria-label={isCollapsed ? "Expand toolbar" : "Collapse toolbar"}
          >
            {isCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
          </button>
        </div>
      )}

      {/* Format bar content */}
      <div className={`flex flex-wrap items-center gap-2 ${isCollapsed && isMobileView ? 'hidden' : ''}`}>
        <div className="flex items-center gap-1 border-r border-sand pr-2">
          <button
            onClick={() => {
              const { from, to } = editor.state.selection;
              const selectedText = editor.state.doc.textBetween(from, to);
              const currentColor = editor.getAttributes('textStyle').color;

              // Toggle bold first
              editor.chain().focus().toggleBold().run();

              // If there's a color and selected text, reapply the color
              if (currentColor && selectedText) {
                // Small delay to ensure the bold formatting is applied first
                setTimeout(() => {
                  editor.chain().focus().setColor(currentColor).run();
                }, 10);
              }
            }}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('bold') ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Bold"
            type="button"
            aria-pressed={editor.isActive('bold')}
          >
            <Bold className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              const { from, to } = editor.state.selection;
              const selectedText = editor.state.doc.textBetween(from, to);
              const currentColor = editor.getAttributes('textStyle').color;

              // Toggle italic first
              editor.chain().focus().toggleItalic().run();

              // If there's a color and selected text, reapply the color
              if (currentColor && selectedText) {
                // Small delay to ensure the italic formatting is applied first
                setTimeout(() => {
                  editor.chain().focus().setColor(currentColor).run();
                }, 10);
              }
            }}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('italic') ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Italic"
            type="button"
            aria-pressed={editor.isActive('italic')}
          >
            <Italic className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              const { from, to } = editor.state.selection;
              const selectedText = editor.state.doc.textBetween(from, to);
              const currentColor = editor.getAttributes('textStyle').color;

              // Toggle underline first
              editor.chain().focus().toggleUnderline().run();

              // If there's a color and selected text, reapply the color
              if (currentColor && selectedText) {
                // Small delay to ensure the underline formatting is applied first
                setTimeout(() => {
                  editor.chain().focus().setColor(currentColor).run();
                }, 10);
              }
            }}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('underline') ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Underline"
            type="button"
            aria-pressed={editor.isActive('underline')}
          >
            <Underline className="h-5 w-5" />
          </button>
          <button
            onClick={clearFormatting}
            className="p-2 rounded hover:bg-sand/20 text-slate"
            title="Clear Formatting"
            type="button"
          >
            <Eraser className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-1 border-r border-sand pr-2">
          <button
            onClick={setParagraph}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('paragraph') && !editor.isActive('heading') ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Paragraph"
            type="button"
            aria-pressed={editor.isActive('paragraph') && !editor.isActive('heading')}
          >
            <Type className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('heading', { level: 1 }) ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Heading 1"
            type="button"
            aria-pressed={editor.isActive('heading', { level: 1 })}
          >
            <Heading1 className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('heading', { level: 2 }) ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Heading 2"
            type="button"
            aria-pressed={editor.isActive('heading', { level: 2 })}
          >
            <Heading2 className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('heading', { level: 3 }) ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Heading 3"
            type="button"
            aria-pressed={editor.isActive('heading', { level: 3 })}
          >
            <Heading3 className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-1 border-r border-sand pr-2">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('bulletList') ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Bullet List"
            type="button"
            aria-pressed={editor.isActive('bulletList')}
          >
            <List className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('orderedList') ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Numbered List"
            type="button"
            aria-pressed={editor.isActive('orderedList')}
          >
            <ListOrdered className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive('blockquote') ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Quote"
            type="button"
            aria-pressed={editor.isActive('blockquote')}
          >
            <Quote className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-1 border-r border-sand pr-2">
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive({ textAlign: 'left' }) ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Align Left"
            type="button"
            aria-pressed={editor.isActive({ textAlign: 'left' })}
          >
            <AlignLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive({ textAlign: 'center' }) ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Align Center"
            type="button"
            aria-pressed={editor.isActive({ textAlign: 'center' })}
          >
            <AlignCenter className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive({ textAlign: 'right' }) ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Align Right"
            type="button"
            aria-pressed={editor.isActive({ textAlign: 'right' })}
          >
            <AlignRight className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={`p-2 rounded hover:bg-sand/20 ${
              editor.isActive({ textAlign: 'justify' }) ? 'bg-sand/20 text-midnight' : 'text-slate'
            }`}
            title="Justify"
            type="button"
            aria-pressed={editor.isActive({ textAlign: 'justify' })}
          >
            <AlignJustify className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-1 border-r border-sand pr-2">
          <button
            onClick={() => {
              if (isInList(editor)) {
                // First check if there's margin indent to remove
                const { $from } = editor.state.selection;
                let hasMarginIndent = false;
                for (let depth = $from.depth; depth > 0; depth--) {
                  const node = $from.node(depth);
                  if (node.type.name === 'listItem' && (node.attrs.indent || 0) > 0) {
                    hasMarginIndent = true;
                    break;
                  }
                }
                if (hasMarginIndent) {
                  editor.chain().focus().outdentListItem().run();
                } else {
                  editor.chain().focus().liftListItem('listItem').run();
                }
              } else {
                editor.chain().focus().outdent().run();
              }
            }}
            className="p-2 rounded hover:bg-sand/20 text-slate"
            title="Decrease Indent (Shift+Tab)"
            type="button"
          >
            <IndentDecrease className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              if (isInList(editor)) {
                // Try sinkListItem first, then fall back to margin indent
                const sunk = editor.chain().focus().sinkListItem('listItem').run();
                if (!sunk) {
                  editor.chain().focus().indentListItem().run();
                }
              } else {
                editor.chain().focus().indent().run();
              }
            }}
            className="p-2 rounded hover:bg-sand/20 text-slate"
            title="Increase Indent (Tab)"
            type="button"
          >
            <IndentIncrease className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-1 border-r border-sand pr-2">
          <div className="relative" ref={colorMenuRef}>
            <button
              onClick={toggleColorMenu}
              className="p-2 rounded hover:bg-sand/20 text-slate flex items-center"
              title="Text Color"
              type="button"
              aria-expanded={isColorOpen}
            >
              <Palette className="h-5 w-5" />
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
            {isColorOpen && (
              <div className="formatbar-dropdown mt-1 w-48">
                <div className="grid grid-cols-2 gap-1">
                  {colors.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => {
                        const { from, to } = editor.state.selection;
                        const selectedText = editor.state.doc.textBetween(from, to);

                        if (selectedText) {
                          // Apply color with inline style to override any formatting
                          editor.chain().focus().setColor(color.value).run();
                          // Also apply the color as an inline style to ensure it persists
                          editor.commands.updateAttributes('textStyle', { color: color.value });
                        } else {
                          editor.chain().focus().setColor(color.value).run();
                        }
                        setIsColorOpen(false);
                      }}
                      className="px-3 py-2 rounded hover:bg-sand/10 text-left flex items-center text-sm"
                      type="button"
                    >
                      <span
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: color.value }}
                      />
                      <span style={{ color: color.value }}>{color.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="relative" ref={highlightMenuRef}>
            <button
              onClick={toggleHighlightMenu}
              className="p-2 rounded hover:bg-sand/20 text-slate flex items-center"
              title="Highlight Color"
              type="button"
              aria-expanded={isHighlightOpen}
            >
              <Highlighter className="h-5 w-5" />
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
            {isHighlightOpen && (
              <div className="formatbar-dropdown mt-1 w-48">
                <div className="grid grid-cols-2 gap-1">
                  {highlights.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => handleHighlight(color.value)}
                      className="px-3 py-2 rounded hover:bg-sand/10 text-left flex items-center text-sm"
                      type="button"
                    >
                      <span
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: color.value }}
                      />
                      <span>{color.name}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => handleHighlight('none')}
                    className="px-3 py-2 rounded hover:bg-sand/10 text-left col-span-2 text-sm"
                    type="button"
                  >
                    Clear Highlight
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="p-2 rounded hover:bg-sand/20 text-slate disabled:opacity-50"
            title="Undo"
            type="button"
            aria-disabled={!editor.can().undo()}
          >
            <Undo className="h-5 w-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="p-2 rounded hover:bg-sand/20 text-slate disabled:opacity-50"
            title="Redo"
            type="button"
            aria-disabled={!editor.can().redo()}
          >
            <Redo className="h-5 w-5" />
          </button>
        </div>

        {/* Link Controls */}
        <div className="flex items-center gap-1 border-l border-sand pl-2">
          <button
            onClick={handleLinkClick}
            className={`p-2 rounded hover:bg-sand/20 ${editor.isActive('link') ? 'bg-electric/20 text-electric' : 'text-slate'}`}
            title="Add Link"
          >
            <Link className="h-5 w-5" />
          </button>

          {editor.isActive('link') && (
            <button
              onClick={handleUnlink}
              className="p-2 rounded hover:bg-sand/20 text-slate"
              title="Remove Link"
            >
              <Unlink className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div ref={linkModalRef} className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <h3 className="text-lg font-semibold text-midnight mb-4">Add Link</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-1">URL</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                  autoFocus
                />
              </div>

              {!editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, ' ') && (
                <div>
                  <label className="block text-sm font-medium text-slate mb-1">Link Text</label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    placeholder="Link text"
                    className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-slate hover:text-midnight"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkSubmit}
                disabled={!linkUrl}
                className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}